import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientsJournee from './pages/ClientsJournee';
import Articles from './pages/Articles';
import Factures from './pages/Factures';
import NouvelleFacture from './pages/NouvelleFacture';
import DetailFacture from './pages/DetailFacture';
import Utilisateurs from './pages/Utilisateurs';
import Preferences from './pages/Preferences';
import MaisonsRepos from './pages/MaisonsRepos';
import EnvoiPeppol from './pages/EnvoiPeppol';
import Layout from './components/Layout';

// Configuration axios pour inclure les cookies
axios.defaults.withCredentials = true;

// Contexte d'authentification
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

// Composant principal de l'application
function AppContent() {
  const { user, login, logout, mustChangePassword, setMustChangePassword } = useAuth();

  const theme = createTheme({
    palette: {
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
    },
  });

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login onLogin={login} />
      </ThemeProvider>
    );
  }

  // Si l'utilisateur doit changer son mot de passe
  if (mustChangePassword) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ChangePassword onPasswordChanged={() => setMustChangePassword(false)} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={!user ? <Login onLogin={login} /> : <Navigate to="/" />} />
          <Route path="/register" element={!user ? <Register /> : <Navigate to="/" />} />
          <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/" />} />
          <Route path="/" element={user ? <Layout user={user} onLogout={logout}><Dashboard /></Layout> : <Navigate to="/login" />} />
          <Route path="/clients" element={user ? <Layout user={user} onLogout={logout}><Clients user={user} /></Layout> : <Navigate to="/login" />} />
          <Route path="/clients/journee" element={user ? <Layout user={user} onLogout={logout}><ClientsJournee /></Layout> : <Navigate to="/login" />} />
          <Route path="/maisons-repos" element={user ? <Layout user={user} onLogout={logout}><MaisonsRepos /></Layout> : <Navigate to="/login" />} />
          <Route path="/articles" element={user ? <Layout user={user} onLogout={logout}><Articles /></Layout> : <Navigate to="/login" />} />
          <Route path="/factures" element={user ? <Layout user={user} onLogout={logout}><Factures /></Layout> : <Navigate to="/login" />} />
          <Route path="/factures/nouvelle" element={user ? <Layout user={user} onLogout={logout}><NouvelleFacture /></Layout> : <Navigate to="/login" />} />
          <Route path="/factures/:id" element={user ? <Layout user={user} onLogout={logout}><DetailFacture /></Layout> : <Navigate to="/login" />} />
          <Route path="/peppol" element={user ? <Layout user={user} onLogout={logout}><EnvoiPeppol /></Layout> : <Navigate to="/login" />} />
          <Route path="/utilisateurs" element={user ? <Layout user={user} onLogout={logout}><Utilisateurs /></Layout> : <Navigate to="/login" />} />
          <Route path="/preferences" element={user ? <Layout user={user} onLogout={logout}><Preferences /></Layout> : <Navigate to="/login" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

// Provider d'authentification
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    // Vérifier si l'utilisateur est déjà connecté
    const checkAuth = async () => {
      try {
        const response = await axios.get('/api/auth/me');
        setUser(response.data.user);
      } catch (error) {
        // L'utilisateur n'est pas connecté
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = (userData, requiresPasswordChange = false) => {
    setUser(userData);
    setMustChangePassword(requiresPasswordChange);
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      setUser(null);
      setMustChangePassword(false);
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    mustChangePassword,
    setMustChangePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App; 